/**
 * Where a visitor came from, kept in the browser until they sign up.
 *
 * Two touches, the standard pair:
 *   first touch  the first source we ever saw for this browser (kept 90 days)
 *   last touch   the latest non-direct source (a direct visit does not erase it)
 *
 * Returns from our own sign-in and payment round trips (Google OAuth, the
 * Supabase auth callback, Stripe) are not sources. Treating them as sources is
 * exactly why Google sign-ups used to show up as "google / referral": the
 * visitor came from LinkedIn, clicked "Continue with Google", and came back
 * from accounts.google.com.
 *
 * On signup the pair is written once to user_attribution (see Onboarding), so
 * every account keeps its real origin no matter what GA does with sessions.
 */

import { hasAnalyticsConsent, onConsentChange } from "@/lib/consent";

export type Touch = {
  source: string;
  medium: string;
  campaign?: string;
  term?: string;
  content?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  fbclid?: string;
  msclkid?: string;
  li_fat_id?: string;
  ttclid?: string;
  referrer_host?: string;
  landing_page: string;
  channel: string;
  ts: string;
};

const FIRST_KEY = "ec_ft";
const LAST_KEY = "ec_lt";
const FIRST_TOUCH_TTL_MS = 90 * 24 * 60 * 60 * 1000;

/** Hosts that hand the visitor back to us mid-flow: never a traffic source. */
const FLOW_HOST =
  /(^|\.)eyecaptain\.io$|(^|\.)supabase\.co$|^accounts\.google\.[a-z.]+$|^accounts\.youtube\.com$|(^|\.)stripe\.com$|(^|\.)stripe\.network$|(^|\.)paypal\.com$|^localhost$|^127\.0\.0\.1$|(^|\.)netlify\.app$/i;

const SEARCH_HOST = /(^|\.)(google|bing|duckduckgo|yahoo|ecosia|baidu|yandex|naver|brave|startpage|qwant)\.[a-z.]+$/i;
const SOCIAL_HOST =
  /(^|\.)(facebook\.com|fb\.com|m\.facebook\.com|l\.facebook\.com|instagram\.com|linkedin\.com|lnkd\.in|t\.co|twitter\.com|x\.com|reddit\.com|youtube\.com|youtu\.be|tiktok\.com|pinterest\.[a-z.]+|threads\.net|bsky\.app|news\.ycombinator\.com)$/i;
const AI_HOST = /(^|\.)(chatgpt\.com|chat\.openai\.com|perplexity\.ai|claude\.ai|gemini\.google\.com|copilot\.microsoft\.com|you\.com|phind\.com)$/i;

const CLICK_IDS = ["gclid", "gbraid", "wbraid", "fbclid", "msclkid", "li_fat_id", "ttclid"] as const;

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return "";
  }
}

function brand(host: string): string {
  // "l.facebook.com" -> "facebook", "www.google.co.uk" -> "google", "lnkd.in" -> "linkedin"
  if (/lnkd\.in$/.test(host)) return "linkedin";
  if (/^t\.co$/.test(host) || /(^|\.)x\.com$/.test(host)) return "twitter";
  if (/youtu\.be$/.test(host)) return "youtube";
  if (/news\.ycombinator\.com$/.test(host)) return "hackernews";
  if (/chat\.openai\.com$/.test(host)) return "chatgpt";
  // gemini.google.com, copilot.microsoft.com: the product, not the parent brand
  if (/^(gemini|copilot)\./.test(host)) return host.split(".")[0];
  const bare = host.replace(/^www\./, "");
  let base = bare.replace(/\.(com?|org|net|gov|edu|ac)?(\.[a-z]{2})?$/, "");
  if (base === bare) base = bare.replace(/\.[a-z0-9-]+$/, ""); // other TLDs: .app, .io, .dev
  const parts = base.split(".").filter(Boolean);
  return parts[parts.length - 1] || host;
}

/** True when the referrer is our own site or one of our auth/payment round trips. */
export function isFlowReferrer(referrer: string): boolean {
  const host = hostOf(referrer);
  return !!host && FLOW_HOST.test(host);
}

/**
 * The touch this page load represents, or null when it is not a new touch
 * (direct visit, internal navigation, return from OAuth or Stripe).
 */
export function classifyTouch(href: string, referrer: string, now = new Date()): Touch | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  const q = url.searchParams;
  const landing_page = url.pathname;
  const ts = now.toISOString();
  const ids: Partial<Touch> = {};
  for (const k of CLICK_IDS) {
    const v = q.get(k);
    if (v) ids[k] = v.slice(0, 200);
  }
  const refHost = hostOf(referrer);
  const external = !!refHost && !FLOW_HOST.test(refHost);
  const referrer_host = external ? refHost : undefined;

  const utmSource = q.get("utm_source");
  if (utmSource) {
    const medium = (q.get("utm_medium") || (ids.gclid ? "cpc" : "referral")).toLowerCase();
    return clean({
      source: utmSource.toLowerCase(),
      medium,
      campaign: q.get("utm_campaign") || undefined,
      term: q.get("utm_term") || undefined,
      content: q.get("utm_content") || undefined,
      ...ids,
      referrer_host,
      landing_page,
      channel: channelFor(utmSource.toLowerCase(), medium),
      ts,
    });
  }

  if (ids.gclid || ids.gbraid || ids.wbraid) return clean({ source: "google", medium: "cpc", ...ids, referrer_host, landing_page, channel: "paid_search", ts });
  if (ids.msclkid) return clean({ source: "bing", medium: "cpc", ...ids, referrer_host, landing_page, channel: "paid_search", ts });
  if (ids.li_fat_id) return clean({ source: "linkedin", medium: "cpc", ...ids, referrer_host, landing_page, channel: "paid_social", ts });
  if (ids.ttclid) return clean({ source: "tiktok", medium: "cpc", ...ids, referrer_host, landing_page, channel: "paid_social", ts });

  if (!external) {
    // fbclid alone with no referrer: a Facebook/Instagram app link.
    if (ids.fbclid) return clean({ source: "facebook", medium: "social", ...ids, landing_page, channel: "organic_social", ts });
    return null;
  }

  if (AI_HOST.test(refHost)) return clean({ source: brand(refHost), medium: "referral", ...ids, referrer_host, landing_page, channel: "ai_assistant", ts });
  if (SEARCH_HOST.test(refHost)) return clean({ source: brand(refHost), medium: "organic", ...ids, referrer_host, landing_page, channel: "organic_search", ts });
  if (SOCIAL_HOST.test(refHost)) return clean({ source: brand(refHost), medium: "social", ...ids, referrer_host, landing_page, channel: "organic_social", ts });
  return clean({ source: refHost.replace(/^www\./, ""), medium: "referral", ...ids, referrer_host, landing_page, channel: "referral", ts });
}

function channelFor(source: string, medium: string): string {
  if (/^(cpc|ppc|paid|paidsearch|paid_search)$/.test(medium)) return /facebook|instagram|linkedin|tiktok|twitter|reddit|meta/.test(source) ? "paid_social" : "paid_search";
  if (/paid_?social|paidsocial/.test(medium)) return "paid_social";
  if (/^(email|newsletter|e-mail)$/.test(medium)) return "email";
  if (/social/.test(medium)) return "organic_social";
  // A link we sent in a DM (comment → DM replies: utm_medium=dm).
  if (/^(dm|direct_message)$/.test(medium)) return /facebook|instagram|linkedin|tiktok|twitter|threads|meta/.test(source) ? "organic_social" : "other";
  if (/affiliate|partner/.test(medium)) return "affiliate";
  if (/referral/.test(medium)) return "referral";
  if (/display|banner|cpm/.test(medium)) return "display";
  if (/organic/.test(medium)) return "organic_search";
  return "other";
}

function clean<T extends Record<string, unknown>>(o: T): Touch {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(o)) {
    if (v === undefined || v === null || v === "") continue;
    out[k] = typeof v === "string" ? v.slice(0, 300) : v;
  }
  return out as Touch;
}

/*
  Attribution is analytics, so it is only written to localStorage with
  analytics consent (PECR). Without consent the touches live in memory for
  this page load; that is still enough to attribute a signup that happens in
  the same visit. Granting consent later persists them; withdrawing it clears
  them from the device.
*/
const memory = new Map<string, Touch>();

function read(key: string): Touch | null {
  if (!hasAnalyticsConsent()) {
    const mem = memory.get(key);
    if (mem) return mem;
  }
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as Touch) : null;
  } catch {
    return null;
  }
}

function write(key: string, t: Touch): void {
  if (!hasAnalyticsConsent()) {
    memory.set(key, t);
    return;
  }
  try {
    localStorage.setItem(key, JSON.stringify(t));
  } catch {
    /* storage blocked */
  }
}

onConsentChange(() => {
  try {
    if (hasAnalyticsConsent()) {
      for (const [k, t] of memory) localStorage.setItem(k, JSON.stringify(t));
      memory.clear();
    } else {
      for (const k of [FIRST_KEY, LAST_KEY]) {
        const raw = localStorage.getItem(k);
        if (raw && !memory.has(k)) memory.set(k, JSON.parse(raw) as Touch);
        localStorage.removeItem(k);
      }
    }
  } catch {
    /* ignore */
  }
});

/** Run once per page load, before the router touches the URL. */
export function captureAttribution(href = window.location.href, referrer = document.referrer, now = new Date()): void {
  const touch = classifyTouch(href, referrer, now);
  const first = read(FIRST_KEY);
  const firstExpired = !first || !first.ts || now.getTime() - Date.parse(first.ts) > FIRST_TOUCH_TTL_MS;

  if (touch) {
    if (firstExpired) write(FIRST_KEY, touch);
    write(LAST_KEY, touch);
    return;
  }
  // Only a genuine direct landing becomes a first touch. A return from OAuth
  // or Stripe on a fresh browser is not "direct": leave it unknown.
  if (firstExpired && !isFlowReferrer(referrer)) {
    let landing = "/";
    try {
      landing = new URL(href).pathname;
    } catch {
      /* ignore */
    }
    write(FIRST_KEY, { source: "(direct)", medium: "(none)", landing_page: landing, channel: "direct", ts: now.toISOString() });
  }
}

export function getAttribution(): { first: Touch | null; last: Touch | null } {
  const first = read(FIRST_KEY);
  return { first, last: read(LAST_KEY) ?? first };
}
